import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeGet } from "@culturehq/client";
import { Button, Icon, Loader, Panel, Table, Warning } from "@culturehq/components";
import { LandingPage, Organization } from "chq";

import { useProfile } from "app/context";
import { getOrganizationSubdomain, Trans } from "app/utils";
import ErrorMessage from "components/common/ErrorMessage";
import PageHeader from "components/display/PageHeader";
import LandingPageRow from "./landing-pages/LandingPageRow";
import { StateProps } from "./landing-pages/LandingPageTypes";
import "./landing-pages/landing-pages.scss";
import EmbedStories from "./EmbedStories";

const HeaderTitle = () => (
  <>
    <div className="LandingPageTable__title-text">
      <div className="LandingPageTable__icon-text">
        <div className="PromptIcon">
          <Icon icon="page-filled" />
        </div>
      </div>
    </div>
    <div>
      <Trans>
        <h4>Manage Culture Pages</h4>
        Here&apos;s where you can create and edit your Culture Pages. Pages may be focused on
        themes, open roles, business teams, etc. Each Culture Page has a unique URL at the end
        of your primary page (e.g., company.culturehq.com/sales).
      </Trans>
    </div>
  </>
);

const LandingPageTable = () => {
  const { profile: { organization } } = useProfile();
  const history = useHistory();
  const isMounted = useRef<boolean>();

  const [{ loading, landingPages, error }, setState] = useState<StateProps>({
    landingPages: [],
    loading: false,
    error: undefined
  });

  const subdomain = useMemo(() => (
    getOrganizationSubdomain(organization as Organization)
  ), [organization]);

  const handleState = (
    newProps: Partial<StateProps>
  ) => setState(prev => ({ ...prev, ...newProps }));

  const handleFetch = () => {
    handleState({ loading: true });
    makeGet("/landing_pages").then(
      ({ landingPages }: any) => isMounted.current
      && handleState({ landingPages, loading: false }));
  };

  useEffect(() => {
    isMounted.current = true;
    handleFetch();

    return () => {
      isMounted.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Panel limitWidth>
      <Panel.Body className="LandingPageTable">
        <PageHeader title="">
          <HeaderTitle />
        </PageHeader>
        <div className="LandingPageTable__buttons">
          <Button icon="plus" primary onClick={() => history.push("/admin/landing-pages/new")}>
            <Trans>Create New Page</Trans>
          </Button>
          <EmbedStories
            botToken={organization.id}
            orgName={subdomain}
            selectedStories={[]}
          />
        </div>

        {error && <ErrorMessage error={error} />}
        <Loader loading={loading}>
          <Table className="no-scrollbar">
            <thead>
              <tr>
                <th><Trans>Page Name</Trans></th>
                <th><Trans>URL</Trans></th>
                <th><Trans>Created</Trans></th>
                <th><Trans>Updated</Trans></th>
                <th><Trans># Stories</Trans></th>
                <th className="LandingPageTable__actions"><Trans>Actions</Trans></th>
              </tr>
            </thead>
            <tbody>
              {landingPages.map((landingPage: LandingPage) => (
                <LandingPageRow
                  key={landingPage.id}
                  landingPage={landingPage}
                  onFetch={handleFetch}
                />
              ))}
            </tbody>
          </Table>

          {!landingPages.length && (
            <Warning><Trans>No results to show.</Trans></Warning>
          )}
        </Loader>
      </Panel.Body>
    </Panel>
  );
};

export default LandingPageTable;
